import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowLeft, Save, Upload, Plus, Trash2, GripVertical, Lock, Loader2,
  History, X, ChevronDown, Settings2, Eye,
} from "lucide-react";

const FIELD_TYPES = [
  "short_text", "long_text", "email", "phone", "number", "url",
  "select", "multi_select", "radio", "checkbox", "consent", "date",
  "file", "image", "country", "category", "channel", "hidden",
];
const HAS_OPTIONS = ["select", "multi_select", "radio"];

function newField(type) {
  return {
    id: `tmp-${Math.random().toString(36).slice(2)}`,
    key: "", type, label_en: "", label_fr: "", placeholder_en: "", placeholder_fr: "",
    help_en: "", help_fr: "", required: false, default_value: "",
    validation: {}, visibility: null, options: [], protected: false,
  };
}

function FieldCard({ field, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const [open, setOpen] = useState(false);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const set = (k, v) => onChange({ ...field, [k]: v });

  return (
    <div ref={setNodeRef} style={style} className="bg-slate-950 border border-slate-800 rounded-xl" data-testid={`field-card-${field.key || field.type}`}>
      <div className="flex items-center gap-2 p-3">
        <button {...attributes} {...listeners} className="cursor-grab text-slate-600 hover:text-slate-300" title="Drag"><GripVertical size={16} /></button>
        <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-400">{field.type}</span>
        <span className="text-sm text-white font-medium flex-1 truncate">{field.label_en || field.key || "Untitled field"}</span>
        {field.protected && <Lock size={13} className="text-amber-400" title="Protected — cannot be removed" />}
        {field.required && <span className="text-[10px] text-red-400">required</span>}
        <button onClick={() => setOpen(!open)} className="p-1.5 text-slate-400 hover:text-white"><ChevronDown size={16} className={cn("transition-transform", open && "rotate-180")} /></button>
        {!field.protected && <button onClick={onRemove} className="p-1.5 text-slate-400 hover:text-red-400" data-testid={`field-remove-${field.key || field.type}`}><Trash2 size={15} /></button>}
      </div>
      {open && (
        <div className="p-3 pt-0 grid grid-cols-2 gap-3 border-t border-slate-800/60">
          <Inp label="Field Key" value={field.key} onChange={(v) => set("key", v)} disabled={field.protected} />
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" className="accent-blue-500" checked={field.required} disabled={field.protected} onChange={(e) => set("required", e.target.checked)} /> Required
            </label>
          </div>
          <Inp label="Label (EN)" value={field.label_en} onChange={(v) => set("label_en", v)} />
          <Inp label="Label (FR)" value={field.label_fr} onChange={(v) => set("label_fr", v)} />
          <Inp label="Placeholder (EN)" value={field.placeholder_en} onChange={(v) => set("placeholder_en", v)} />
          <Inp label="Placeholder (FR)" value={field.placeholder_fr} onChange={(v) => set("placeholder_fr", v)} />
          <Inp label="Help (EN)" value={field.help_en} onChange={(v) => set("help_en", v)} />
          <Inp label="Help (FR)" value={field.help_fr} onChange={(v) => set("help_fr", v)} />
          {["short_text", "long_text"].includes(field.type) && (
            <>
              <Inp label="Min Length" type="number" value={field.validation?.min_length || ""} onChange={(v) => set("validation", { ...field.validation, min_length: v })} />
              <Inp label="Max Length" type="number" value={field.validation?.max_length || ""} onChange={(v) => set("validation", { ...field.validation, max_length: v })} />
            </>
          )}
          {["file", "image"].includes(field.type) && (
            <>
              <Inp label="Allowed Types" value={field.validation?.allowed_file_types || ""} onChange={(v) => set("validation", { ...field.validation, allowed_file_types: v })} />
              <Inp label="Max File Size (MB)" type="number" value={field.validation?.max_file_size || ""} onChange={(v) => set("validation", { ...field.validation, max_file_size: v })} />
            </>
          )}
          {HAS_OPTIONS.includes(field.type) && (
            <div className="col-span-2">
              <label className="text-xs text-slate-500 block mb-1">Options (one per line: value|EN|FR)</label>
              <textarea rows={3} className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white text-sm"
                value={(field.options || []).map((o) => `${o.value}|${o.label_en || ""}|${o.label_fr || ""}`).join("\n")}
                onChange={(e) => set("options", e.target.value.split("\n").filter(Boolean).map((l) => { const [value, le, lf] = l.split("|"); return { value: value?.trim() || "", label_en: le?.trim() || value, label_fr: lf?.trim() || le || value }; }))} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Inp({ label, value, onChange, type = "text", disabled }) {
  return (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <input type={type} disabled={disabled} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className={cn("w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20", disabled && "opacity-50 cursor-not-allowed")} />
    </div>
  );
}

export default function FormEditor() {
  const { formId } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState("fields");
  const [addOpen, setAddOpen] = useState(false);
  const [showRevs, setShowRevs] = useState(false);
  const [toast, setToast] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const notify = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/forms/${formId}`, { withCredentials: true })
      .then((r) => { setForm(r.data); setDraft(r.data.draft); })
      .catch(() => notify("Failed to load form"))
      .finally(() => setLoading(false));
  }, [formId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const setMeta = (k, v) => { setDraft((d) => ({ ...d, [k]: v })); setDirty(true); };
  const setFields = (fields) => { setDraft((d) => ({ ...d, fields })); setDirty(true); };

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      const oldI = draft.fields.findIndex((f) => f.id === active.id);
      const newI = draft.fields.findIndex((f) => f.id === over.id);
      setFields(arrayMove(draft.fields, oldI, newI));
    }
  };

  const addField = (type) => { setAddOpen(false); setFields([...(draft.fields || []), newField(type)]); };
  const updateField = (i, f) => { const arr = [...draft.fields]; arr[i] = f; setFields(arr); };
  const removeField = (i) => setFields(draft.fields.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/forms/${formId}`, { draft }, { withCredentials: true });
      setDirty(false); notify("Draft saved"); load();
    } catch (e) { notify(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setSaving(true);
    try {
      if (dirty) await axios.put(`${API}/admin/forms/${formId}`, { draft }, { withCredentials: true });
      const r = await axios.post(`${API}/admin/forms/${formId}/publish`, {}, { withCredentials: true });
      const v = r.data?.version;
      setDirty(false);
      notify(v ? `Form published successfully. The live site is now using version ${v}.` : "Published to live");
      load();
    } catch (e) { notify(e.response?.data?.detail || "Publish failed"); }
    finally { setSaving(false); }
  };

  const restoreRev = async (version) => {
    await axios.post(`${API}/admin/forms/${formId}/restore-revision`, { version }, { withCredentials: true });
    setShowRevs(false); notify(`Restored v${version} to draft`); load();
  };

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="animate-spin text-slate-500" /></div>;
  if (!form || !draft) return <div className="text-center py-16 text-slate-500">Form not found. <Link to="/admin/forms" className="text-blue-400">Back to forms</Link></div>;

  const managed = form.managed_workflow === "submit_channel";

  return (
    <div data-testid="form-editor">
      {toast && <div className="fixed top-4 right-4 z-[200] bg-slate-800 border border-slate-700 text-white text-sm px-4 py-2.5 rounded-xl shadow-xl" data-testid="editor-toast">{toast}</div>}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => { if (!dirty || window.confirm("Discard unsaved changes?")) nav("/admin/forms"); }} className="p-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-400 hover:text-white"><ArrowLeft size={18} /></button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-white">{form.name_en}</h2>
              {form.is_protected_auth && <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20"><Lock size={11} /> Protected</span>}
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
              <span>{form.public_route}</span>
              <span className="text-emerald-400">Live v{form.published ? form.version : "—"}</span>
              <span className="text-blue-400">Draft v{form.published ? form.version + 1 : (form.version || 1)}</span>
              {dirty && <span className="text-amber-400">• unsaved changes</span>}
              {!dirty && form.published && form.draft && JSON.stringify(form.draft) !== JSON.stringify(form.published) &&
                <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20" data-testid="unpublished-changes-badge">Unpublished changes</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => window.open(`${window.location.origin}/forms-preview/${formId}`, "_blank")}
            data-testid="editor-preview-draft" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 text-sm hover:text-white"><Eye size={15} /> Preview Draft</button>
          {form.public_route && form.status === "published" && (
            <a href={form.public_route} target="_blank" rel="noreferrer" data-testid="editor-view-live"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 text-sm hover:text-white"><Eye size={15} /> View Live</a>
          )}
          <button onClick={() => setShowRevs(true)} data-testid="editor-revisions-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 text-sm hover:text-white"><History size={15} /> History</button>
          <button onClick={save} disabled={saving} data-testid="editor-save-draft" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 disabled:opacity-50">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Draft</button>
          <button onClick={publish} disabled={saving} data-testid="editor-publish" className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-500 disabled:opacity-50"><Upload size={15} /> Publish</button>
        </div>
      </div>

      {managed && (
        <div className="mb-6 rounded-xl border border-teal-500/20 bg-teal-500/5 px-4 py-3 text-sm text-teal-300" data-testid="managed-notice">
          This is a managed form. Its submissions are handled by the dedicated <Link to="/admin/channel-submissions" className="underline">Channel Submissions</Link> review queue. You can edit its labels and messages here.
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-800">
        {[["fields", "Fields", Settings2], ["content", "Content & Messages", Eye]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`editor-tab-${k}`}
            className={cn("flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors", tab === k ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-white")}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "content" && (
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-5 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
          {[["title_en", "Title (EN)"], ["title_fr", "Title (FR)"], ["description_en", "Description (EN)"], ["description_fr", "Description (FR)"],
            ["submit_en", "Submit Button (EN)"], ["submit_fr", "Submit Button (FR)"], ["success_en", "Success Message (EN)"], ["success_fr", "Success Message (FR)"],
            ["error_en", "Error Message (EN)"], ["error_fr", "Error Message (FR)"], ["notification_recipients", "Notification Recipients (emails)"]].map(([k, label]) => (
            <Inp key={k} label={label} value={draft[k]} onChange={(v) => setMeta(k, v)} />
          ))}
          <Inp label="Rate Limit (per hour)" type="number" value={draft.rate_limit} onChange={(v) => setMeta("rate_limit", Number(v))} />
          <div className="col-span-full flex flex-wrap gap-5 pt-2">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" className="accent-blue-500" checked={!!draft.confirmation_email_enabled} onChange={(e) => setMeta("confirmation_email_enabled", e.target.checked)} /> Confirmation email</label>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" className="accent-blue-500" checked={!!draft.admin_notification_enabled} onChange={(e) => setMeta("admin_notification_enabled", e.target.checked)} /> Admin notification</label>
            {form.key === "contact" && <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" className="accent-blue-500" checked={!!draft.link_to_chat} onChange={(e) => setMeta("link_to_chat", e.target.checked)} /> Link submissions to Chat</label>}
            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-not-allowed" title="Enable when a captcha provider key is configured"><input type="checkbox" disabled checked={false} /> Captcha (needs provider key)</label>
          </div>
        </div>
      )}

      {tab === "fields" && (
        <div className="max-w-3xl">
          {managed ? (
            <p className="text-slate-500 text-sm">Field editing is disabled for managed forms.</p>
          ) : (
            <>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={(draft.fields || []).map((f) => f.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2 mb-4" data-testid="fields-list">
                    {(draft.fields || []).map((f, i) => (
                      <FieldCard key={f.id} field={f} onChange={(nf) => updateField(i, nf)} onRemove={() => removeField(i)} />
                    ))}
                    {(draft.fields || []).length === 0 && <p className="text-slate-500 text-sm py-8 text-center border border-dashed border-slate-800 rounded-xl">No fields yet. Add one below.</p>}
                  </div>
                </SortableContext>
              </DndContext>
              <div className="relative inline-block">
                <button onClick={() => setAddOpen(!addOpen)} data-testid="add-field-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-500"><Plus size={16} /> Add Field</button>
                {addOpen && (
                  <div className="absolute left-0 top-12 z-20 w-56 max-h-72 overflow-y-auto bg-slate-800 border border-slate-700 rounded-xl shadow-2xl py-1" data-testid="add-field-menu">
                    {FIELD_TYPES.map((t) => (
                      <button key={t} onClick={() => addField(t)} data-testid={`add-field-${t}`} className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 capitalize">{t.replace(/_/g, " ")}</button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Revision history */}
      {showRevs && (
        <div className="fixed inset-0 z-[100] flex justify-end bg-slate-950/70 backdrop-blur-sm" onClick={() => setShowRevs(false)}>
          <div className="w-full max-w-md bg-slate-900 h-full border-l border-slate-800 overflow-y-auto animate-slideLeft" onClick={(e) => e.stopPropagation()} data-testid="revisions-panel">
            <div className="sticky top-0 bg-slate-950/95 border-b border-slate-800 p-5 flex items-center justify-between">
              <h3 className="font-bold text-white">Revision History</h3>
              <button onClick={() => setShowRevs(false)} className="text-slate-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-2">
              {(form.revisions || []).length === 0 && <p className="text-slate-500 text-sm">No published revisions yet.</p>}
              {[...(form.revisions || [])].reverse().map((r) => (
                <div key={r.version} className="flex items-center justify-between rounded-lg bg-slate-950 px-3 py-2.5">
                  <div>
                    <div className="text-sm text-white font-medium">Version {r.version}</div>
                    <div className="text-xs text-slate-500">{(r.published_at || "").slice(0, 16).replace("T", " ")} • {r.published_by}</div>
                  </div>
                  <button onClick={() => restoreRev(r.version)} data-testid={`restore-rev-${r.version}`} className="text-xs px-3 py-1.5 rounded-lg bg-slate-800 text-blue-400 hover:bg-slate-700">Restore to draft</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
